/**
 * Random / % split — the shares, as a step row like Schedule's: equal,
 * manual %, or by the gateway formula (each ranked gateway's share of the
 * positive scores; one a rule excludes gets the average share so it keeps
 * learning). The bar draws them; Split adds the day's exact quotas. A
 * gateway a rule set aside or took out draws nothing (Phase 19) — the line
 * under the bar names it.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Percent } from 'lucide-react';

import type { DraftGateway } from '../../../../drop-generator.types';
import { ChoiceChips } from '../../general/controls/ChoiceChips';
import { StepRow, ValueLine } from '../../general/controls/StepRow';
import type { GatewayOp } from '../../state/gateway-draft';
import { WEIGHTS_HINT } from '../cards/strategy-copy';
import type { RotationView } from '../ranking/formula-pool';
import { WeightsBar } from '../WeightsBar';
import { RuleOutcomeLine } from './RuleOutcomeLine';

const OPTIONS = [
  { value: 'OFF', label: 'Equal' },
  { value: 'MANUAL', label: 'Manual %' },
  { value: 'FORMULA', label: 'By formula' },
] as const;
type WeightsMode = (typeof OPTIONS)[number]['value'];

interface Props {
  gateway: DraftGateway;
  shares: number[];
  view: RotationView;
  splitOver: number;
  onOp: (op: GatewayOp) => void;
}

export const WeightsZone: FC<Props> = ({ gateway, shares, view, splitOver, onOp }) => (
  <StepRow icon={Percent} label="Weights" hint={WEIGHTS_HINT}>
    <ChoiceChips options={OPTIONS} value={gateway.weightsMode as WeightsMode} onChange={(weightsMode) => onOp({ kind: 'patch', patch: { weightsMode } })} ariaLabel="Gateway shares" />
    <ValueLine key={gateway.weightsMode}>
      {gateway.pool.length === 0 ? (
        <span className="text-xs text-muted-foreground">Add gateways to the pool to shape the distribution.</span>
      ) : (
        <div className="w-full">
          <WeightsBar pool={gateway.pool} shares={shares} splitOver={gateway.strategy === 'SPLIT' ? splitOver : null} />
        </div>
      )}
    </ValueLine>
    <RuleOutcomeLine gateway={gateway} view={view} />
    {gateway.weightsMode === 'MANUAL' ? <p className="text-xs text-muted-foreground">Set each gateway’s % on the rows in Gateways — they are scaled to add up to 100.</p> : null}
  </StepRow>
);
